import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useVirtualizer } from '@tanstack/react-virtual'
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { components } from 'react-select'
import { menuListInnerStyle, optionHeight, optionInlineStyle } from '~/components/react_multi_select/styles'

export function ValueContainer(props) {
  const { selectProps, children } = props
  const { value, isSelectAllSelected } = selectProps

  const renderPlaceholder = value.length === 0

  return (
    <components.ValueContainer {...props}>
      {renderPlaceholder && (
        <span className='text-muted'>
          <Trans>Select…</Trans>
        </span>
      )}
      {isSelectAllSelected && 'All items are selected'}
      {children}
    </components.ValueContainer>
  )
}

export function Placeholder() {
  return null // a custom placeholder is rendered directly in the ValueContainer
}

export function MultiValue(props) {
  const { index, children, selectProps } = props
  const { value, isSelectAllSelected } = selectProps

  if (isSelectAllSelected) {
    // do not render individual options when "select all" is selected
    return null
  }

  let sep = ', '

  if (index === value.length - 1) {
    // check if last item in the list
    sep = ''
  }

  return (
    <>
      {children}
      {sep}
    </>
  )
}

function SearchInput({ value, onBlur, onChange, onFocus, onMouseDown }) {
  return (
    <div className='input-icon'>
      <input
        autoCapitalize='none'
        autoComplete='off'
        autoCorrect='off'
        className='form-control'
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        onMouseDown={onMouseDown}
        onTouchEnd={onMouseDown}
        placeholder={t`Search…`}
        spellCheck='false'
        type='text'
        value={value}
      />
      <span className='input-icon-addon'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='24'
          height='24'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          className='icon icon-tabler icons-tabler-outline icon-tabler-search m-0'
          role='presentation'
        >
          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
          <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
          <path d='M21 21l-6 -6' />
        </svg>
      </span>
    </div>
  )
}

export function Menu(props) {
  const { selectProps, innerProps, children } = props
  const {
    inputValue,
    onMenuBlur,
    onMenuMouseDown,
    onSearchInputBlur,
    onSearchInputChange,
    onSearchInputFocus,
    onSearchInputMouseDown,
  } = selectProps

  // biome-ignore lint/correctness/useExhaustiveDependencies: todo: re-evaluate dependencies
  const newInnerProps = useMemo(
    () => ({
      ...innerProps,
      onBlur: onMenuBlur, // add a new inner prop
      onMouseDown: onMenuMouseDown, // override onMouseDown but keep other inner props
    }),
    [innerProps],
  )

  return (
    <components.Menu {...props} innerProps={newInnerProps}>
      <div className='p-2'>
        <SearchInput
          value={inputValue}
          onBlur={onSearchInputBlur}
          onChange={onSearchInputChange}
          onFocus={onSearchInputFocus}
          onMouseDown={onSearchInputMouseDown}
        />
      </div>

      {children}
    </components.Menu>
  )
}

// since react-select does not easily allow props to be passed to specific components, a React
// context is necessary to pass the virtual item from the <MenuList> to the <Option> component
const OptionContext = createContext()

function MenuListInner({ virtualizer, children }) {
  const style = menuListInnerStyle(virtualizer.getTotalSize())

  return (
    <div style={style}>
      {virtualizer.getVirtualItems().map((virtualItem) => (
        <OptionContext.Provider key={virtualItem.key} value={virtualItem}>
          {children(virtualItem)}
        </OptionContext.Provider>
      ))}
    </div>
  )
}

export function MenuList(props) {
  const { innerRef, children } = props

  const scrollableDivRef = useRef(null) // a reference for the scrollable <div> element rendered by the component
  const childrenAsArray = React.Children.toArray(children) // convert the opaque data structure into an array

  const virtualizer = useVirtualizer({
    count: childrenAsArray.length, // how many options are in the menu
    getScrollElement: () => scrollableDivRef.current,
    estimateSize: () => optionHeight,
    overscan: 5,
  })

  // hook into the original innerRef() function to get a reference to the scrollable <div> element
  const newInnerRef = useCallback(
    (ref) => {
      scrollableDivRef.current = ref
      innerRef(ref) // keep the original scroll behavior
    },
    [innerRef],
  )

  return (
    <components.MenuList {...props} innerRef={newInnerRef}>
      <MenuListInner virtualizer={virtualizer}>{(virtualItem) => childrenAsArray[virtualItem.index]}</MenuListInner>
    </components.MenuList>
  )
}

function noop() {}

export function Option(props) {
  const virtualItem = useContext(OptionContext)
  const { isDisabled, isSelected, innerProps, children } = props

  const newInnerProps = {
    ...innerProps,
    'aria-checked': isSelected,
    style: optionInlineStyle(virtualItem.start),
  }

  return (
    <components.Option {...props} innerProps={newInnerProps}>
      <input type='checkbox' checked={isSelected} disabled={isDisabled} onChange={noop} className='form-check-input' />
      {children}
    </components.Option>
  )
}
